<script lang="ts">
import {RouteNames} from "@/utils/constants.ts";

export function getRouteXDashboard() {
  return {
    name: RouteNames.X_DASHBOARD
  };
}
</script>

<script setup lang="ts">
import BreadCrumbs from "../../components/BreadCrumbs.vue";
import MButton from "../../components/buttons/MButton.vue";
import BasicCard from "../../components/BasicCard.vue";
import CurrencySelector from "../../components/CurrencySelector.vue";

import {
  IconBtc,
  IconEth,
  IconUsdt,
  IconXrp
} from "@iconify-prerendered/vue-cryptocurrency-color";
import PortfolioTable from "../../components/functional/PortfolioTable.vue";
import TransactionsTableWidget from "../../widgets/TransactionsTableWidget.vue";
import DashboardBalancesWidget from "../../widgets/dashboard/DashboardBalancesWidget.vue";
import {getRouteXAccountsCardOrder} from "./accounts/card/OrderCard.vue";
import {getRouteXAccountsFiatSend} from "./accounts/fiat/Send.vue";
import {getRouteXAccountsCryptoSend} from "./accounts/crypto/SendCrypto.vue";
import {getRouteXAccountsCryptoBuy} from "./accounts/crypto/BuyCrypto.vue";
import {getRouteXAccountsCard} from "./accounts/Cards.vue";
import {getRouteXAccountsCrypto} from "./accounts/Crypto.vue";
import IconTopRight from "../../components/icons/IconTopRight.vue";
import IconDollarExchange from "../../components/icons/IconDollarExchange.vue";
import PersonVerifiedBanner from "../../components/PersonVerifiedBanner.vue";
import {usePersonStore} from "@/store";
import {storeToRefs} from "pinia";
import {computed} from "vue";

const personStore = usePersonStore();
const { person } = storeToRefs(personStore);

// Check if user is authenticated
const isAuthenticated = computed(() => person.value !== null);

// Check if user has completed verification (IN_REVIEW or VERIFIED status)
const hasCompletedVerification = computed(() => {
  if (!person.value?.profile) {
    return false;
  }
  const status = person.value.profile.status;
  return status === "VERIFIED" || status === "IN_REVIEW";
});

// Show banner for authenticated users who haven't completed verification
const showOnboardingBanner = computed(() => isAuthenticated.value && !hasCompletedVerification.value);

// For guest users, show dashboard as demo (all interactions disabled)
// Authenticated users always have full access to dashboard
const isGuestMode = computed(() => !isAuthenticated.value);

const accounts: string[] = [];


type ICryptoAccount = {
  icon: any;
  title: string;
  price: number;
  change: number;
};

type ICurrency = {
  char: string;
  name: string;
  exponent: number;
};
type ICashAccount = {
  ccy: ICurrency;
  amount: number;
  color: string;
};
const accountsCrypto: ICryptoAccount[] = [
  {
    icon: IconBtc,
    title: "Bitcoin (BTC)",
    price: 52726.19,
    change: -3.94
  },
  {
    icon: IconEth,
    title: "Ethereum (ETH)",
    price: 52726.19,
    change: 3.94
  },
  {
    icon: IconUsdt,
    title: "TetherUS (USDT)",
    price: 52726.19,
    change: 3.94
  },
  {
    icon: IconXrp,
    title: "Ripple (XRP)",
    price: 52726.19,
    change: -3.94
  }
];

const accountsCash: ICashAccount[] = [
  {
    ccy: {
      char: "€",
      name: "EUR",
      exponent: 2
    },
    color: "#003399",
    amount: 0
  },
  {
    ccy: {
      char: "$",
      name: "USD",
      exponent: 2
    },
    color: "#6CDE07",
    amount: 0
  },
  {
    ccy: {
      char: "£",
      name: "GBP",
      exponent: 2
    },
    color: "#BC3FE0",
    amount: 0
  }
];

</script>

<template>
  <div>
    <!-- Banner for guest users to encourage login -->
    <div v-if="isGuestMode" class="mb-5 min-h-[86px] w-full rounded-2xl shadow p-4 bg-gradient-to-r from-blue-500 to-purple-600">
      <div class="w-full text-white text-lg md:text-2xl font-bold font-['Sora'] leading-[38px] flex flex-col md:flex-row md:justify-between md:items-center items-center gap-4">
        <div class="flex items-center gap-3 text-center md:text-left w-full md:w-auto">
          <span class="text-4xl">👋</span>
          <span>Welcome to Moneterum! Login to access all features</span>
        </div>
      </div>
    </div>

    <!-- Banner for authenticated users who haven't completed onboarding -->
    <PersonVerifiedBanner v-else-if="showOnboardingBanner" class="mb-5" />

    <!-- Dashboard content - shown to all users, but disabled only for guests -->
    <div :class="{ 'pointer-events-none opacity-60 select-none': isGuestMode }">
      <BreadCrumbs
          :title="$t('Dashboard')"
          :accounts="accounts"
          class="mb-5"
      >
        <template v-slot:buttons>
          <CurrencySelector />

          <MButton size2="medium" kind="secondary" :to="getRouteXAccountsCryptoBuy()">Buy / Sell Crypto</MButton>

          <MButton size2="medium" kind="secondary" :to="getRouteXAccountsCrypto()">Receive Crypto</MButton>
        </template>
      </BreadCrumbs>

      <DashboardBalancesWidget/>

      <div class="mt-6 flex flex-col lg:flex-row gap-6">
        <div class="flex flex-col gap-6 w-full lg:w-2/3">
          <div class="min-h-[164px]">
            <BasicCard title="Payments and Transfers" class="">
              <div class="justify-end items-end gap-4 flex flex-col sm:flex-row">
                <MButton
                    kind="secondary"
                    size2="medium"
                    :to="getRouteXAccountsFiatSend()"
                    class="flex-1 w-full sm:w-auto"
                >
                  Send Cash
                  <IconTopRight/>
                </MButton>
                <MButton
                    kind="secondary"
                    size2="medium"
                    :to="getRouteXAccountsCryptoSend()"
                    class="flex-1 w-full sm:w-auto"
                >
                  Send Crypto
                  <IconTopRight/>
                </MButton>
                <MButton
                    kind="default"
                    size2="medium"
                    :to="getRouteXAccountsCryptoBuy()"
                    class="flex-1 w-full sm:w-auto"
                >
                  <IconDollarExchange/>
                  Exchange Cash
                </MButton>
              </div>
            </BasicCard>
          </div>
          <BasicCard title="Transactions">
            <TransactionsTableWidget/>
          </BasicCard>

          <PortfolioTable/>
        </div>

        <div class="flex flex-col gap-6 w-full lg:w-96">
          <BasicCard title="Cards" :highlighted="true" :show-all="getRouteXAccountsCard()">
            <MButton
                kind="dashed"
                size2="medium"
                class="flex-1"
                :to="getRouteXAccountsCardOrder()"
            >
              Add New Card
            </MButton>
          </BasicCard>

          <BasicCard title="Cash Accounts" show-all="accounts.fiat" :collapsible="true">
            <div class="flex flex-col w-full gap-6">
              <div v-for="account of accountsCash" class="flex flex-row justify-between">
                <div class="flex items-center gap-2">
                  <div
                      class="w-10 h-10 rounded-full flex justify-center text-white items-center select-none font-bold text-2xl"
                      :style="{ backgroundColor: account.color }"
                  >
                    {{ account.ccy.char }}
                  </div>
                  <div class="flex flex-col gap-0">
                    <div class="text-zinc-800 text-xl">
                      {{ account.ccy.name }} account
                    </div>
                    <div class="text-gray-400 text-base">{{ account.amount }}</div>
                  </div>
                </div>
              </div>
            </div>
          </BasicCard>

          <BasicCard title="Crypto Wallets" show-all="accounts.crypto" :collapsible="true">
            <div class="flex flex-col w-full gap-6">
              <div v-for="account of accountsCrypto" class="flex flex-row justify-between">
                <div class="flex items-center gap-2">
                  <component :is="account.icon" class="w-10 h-10"/>
                  <div class="flex flex-col gap-0">
                    <div class="text-zinc-800 text-xl">
                      {{ account.title }}
                    </div>
                    <div class="text-gray-400 text-base">{{ account.price }}</div>
                  </div>
                </div>

                <div
                    class="text-base/[38px]"
                    :class="account.change > 0 ? 'text-green-400' : 'text-red-400'"
                >
                  {{ account.change }}
                </div>
              </div>
            </div>
          </BasicCard>
        </div>
      </div>
    </div>
  </div>
</template>
